// src/pages/pentest/dashboard/PentestWebScope.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Grid,
  Paper,
  Button,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate, useParams } from 'react-router-dom';
import { alpha , darken, lighten} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import Nav from './nav';

const typeMeta = {
  login_url: { label: 'LOGIN URL', color: PRIMARYCOLOR, icon: <LockOpenIcon fontSize="small" /> },
  username: { label: 'USERNAME', color: '#7c4dff', icon: <PersonIcon fontSize="small" /> },
};

function normalizeScopeShape(raw) {
  // Accept stringified JSON or nested shapes like { scope: { ... } } or { data: { ... } }
  let s = raw;
  try {
    if (typeof s === 'string') s = JSON.parse(s);
  } catch {
    /* ignore parse errors */
  }
  // Unwrap common wrappers
  if (s && typeof s === 'object') {
    if ('scope' in s && s.scope && typeof s.scope === 'object') s = s.scope;
    if (!s.login_url && !s.credentials && s.data && typeof s.data === 'object') s = s.data;
  }
  return s && typeof s === 'object' ? s : null;
}

const PentestWebScope = () => {
  const [scopeRaw, setScopeRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { pentest_id } = useParams();

  const fetchScope = (token) => {
    fetch(`${BASEURL}/pentest/scope?pentest_id=${pentest_id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.error) {
          if (data.message === 'Invalid token') navigate('/');
          setError(data.message || 'Failed to fetch scope.');
        } else {
          // tolerate: {success, scope}, {scope:{scope:{...}}}, {data:{...}}, or raw object
          const s = (data?.scope?.scope) ?? data?.scope ?? data?.data ?? data ?? null;
          setScopeRaw(s);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching scope:', err);
        setError('Failed to fetch scope.');
        setLoading(false);
      });
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) fetchScope(token);
    else {
      console.error('No token found');
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, pentest_id]);

  const scope = useMemo(() => normalizeScopeShape(scopeRaw), [scopeRaw]);

  // Build items: Login URL + unique usernames
  const items = useMemo(() => {
    if (!scope) return [];
    const out = [];
    if (scope.login_url) out.push({ id: 'login_url', type: 'login_url', value: scope.login_url });
    const usernames = (scope.credentials || [])
      .map((c) => c?.username || c?.email)
      .filter(Boolean);
    [...new Set(usernames)].forEach((u, i) => out.push({ id: `username-${i}`, type: 'username', value: u }));
    return out;
  }, [scope]);

  const kpis = useMemo(() => {
    const loginCount = items.filter((i) => i.type === 'login_url').length;
    const userCount = items.filter((i) => i.type === 'username').length;
    return { loginCount, userCount, total: items.length };
  }, [items]);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {}
  };
  const copyAll = () => copy(items.map((i) => i.value).join('\n'));

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <Nav />

      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        {/* KPI row */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="overline" color="text.secondary">LOGIN URLS</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    icon={<LockOpenIcon />}
                    label="LOGIN URL"
                    size="small"
                    sx={{ bgcolor: alpha(PRIMARYCOLOR, 0.12), color: PRIMARYCOLOR, border: `1px solid ${alpha(PRIMARYCOLOR, 0.35)}` }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>{kpis.loginCount}</Typography>
                </Stack>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="overline" color="text.secondary">USERNAMES</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    icon={<PersonIcon />}
                    label="USERNAME"
                    size="small"
                    sx={{ bgcolor: alpha('#7c4dff', 0.12), color: '#7c4dff', border: `1px solid ${alpha('#7c4dff', 0.35)}` }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>{kpis.userCount}</Typography>
                </Stack>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="overline" color="text.secondary">TOTAL</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label="IN SCOPE"
                    icon={<CheckCircleOutlineIcon />}
                    size="small"
                    sx={{ bgcolor: alpha('#2e7d32', 0.12), color: '#2e7d32', border: `1px solid ${alpha('#2e7d32', 0.35)}` }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>{kpis.total}</Typography>
                </Stack>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Scope card list */}
        <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: 3 }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">Loading scope…</Typography>
            </Stack>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : items.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 4, borderRadius: 3, textAlign: 'center',
                borderStyle: 'dashed', borderColor: (t) => alpha(t.palette.text.primary, 0.2),
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>No scope yet</Typography>
              <Typography variant="body2" color="text.secondary">Waiting for login URL and username…</Typography>
            </Paper>
          ) : (
            <>
              <Grid container spacing={2}>
                {items.map((it) => {
                  const meta = typeMeta[it.type];
                  const isLogin = it.type === 'login_url';

                  return (
                    <Grid key={it.id} item xs={12} md={6} lg={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2, borderRadius: 3, height: '100%',
                          border: '1px solid', borderColor: (t) => alpha(t.palette.divider, 0.6),
                          background: alpha(meta.color, 0.05),
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                            <Chip
                              icon={meta.icon}
                              label={meta.label}
                              size="small"
                              sx={{
                                bgcolor: alpha(meta.color, 0.12),
                                color: meta.color,
                                fontWeight: 700,
                                border: `1px solid ${alpha(meta.color, 0.35)}`,
                              }}
                            />
                            <Chip
                              size="small"
                              label="IN SCOPE"
                              icon={<CheckCircleOutlineIcon fontSize="small" />}
                              sx={{ bgcolor: alpha('#2e7d32', 0.08), color: '#2e7d32', border: '1px solid ' + alpha('#2e7d32', 0.25) }}
                            />
                          </Stack>

                          {/* Primary value */}
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 900, wordBreak: 'break-word', lineHeight: 1.2 }}
                            title={it.value}
                          >
                            {it.value}
                          </Typography>

                          <Divider sx={{ my: 1 }} />

                          {/* Actions */}
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              {isLogin && (
                                <Tooltip title="Open in browser">
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={it.value}
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{ border: '1px solid', borderColor: alpha(meta.color, 0.35), borderRadius: 1.5 }}
                                  >
                                    <LinkIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Copy value">
                                <IconButton
                                  size="small"
                                  onClick={() => copy(it.value)}
                                  sx={{ border: '1px solid', borderColor: alpha(meta.color, 0.35), borderRadius: 1.5 }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>

                            <Typography variant="caption" color="text.secondary">Raw: {it.value}</Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              <Divider sx={{ my: 3 }} />
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  {items.length} item{items.length !== 1 ? 's' : ''} in scope • Login URLs {kpis.loginCount} • Usernames {kpis.userCount}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={copyAll} startIcon={<ContentCopyIcon />}>Copy full scope</Button>
                </Stack>
              </Stack>
            </>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default PentestWebScope;
